import { useMemo } from 'react';
import { useMutation } from '@apollo/react-hooks';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ValidationError } from '@atlassian/jira-fetch';
import { UNLINK_ISSUES_EXPERIENCE } from '../../common/constants';
import { useIssuesMap } from '../../common/utils/use-issues-map';
import { useUpdateIssueLinksInCache } from '../update-dependencies-in-cache';
import type {
	RemoveJWMDependencyTimelineView,
	RemoveJWMDependencyTimelineViewVariables,
} from './__generated_apollo__/RemoveJWMDependencyTimelineView';
import { REMOVE_DEPENDENCY_TIMELINE_MUTATION } from './gql';
import type { UseRemoveDependencyInput, UseRemoveDependencyReturn } from './types';
import { createOptimisticLinkItems } from './utils';

export const fieldIdsToReturn = ['issuelinks'] as const;

const QUERY_NAME = 'removeDependencyTimelineView';

const getRelevantError = (data?: RemoveJWMDependencyTimelineView | null) => {
	if (data == null || data.unlinkJwmItems?.success === true) {
		return undefined;
	}

	const firstError = data.unlinkJwmItems?.errors?.[0];

	if (firstError != null) {
		// if the issue is not found, do not consider it as an error for deleting
		if (firstError.extensions?.statusCode === 404) {
			return undefined;
		}

		// if there is an error message, show it to the user
		if (firstError.message != null) {
			return new ValidationError(firstError.message);
		}
	}

	return undefined;
};

export const useRemoveDependency = (): UseRemoveDependencyReturn => {
	const { updateIssueLinksInCache } = useUpdateIssueLinksInCache();
	const [{ issuesMap }] = useIssuesMap();
	const start = useExperienceStart(UNLINK_ISSUES_EXPERIENCE);
	const onSuccess = useExperienceSuccess(UNLINK_ISSUES_EXPERIENCE);
	const onFail = useExperienceFail(UNLINK_ISSUES_EXPERIENCE);

	const [removeDependency, { data, loading, error }] = useMutation<
		RemoveJWMDependencyTimelineView,
		RemoveJWMDependencyTimelineViewVariables
	>(REMOVE_DEPENDENCY_TIMELINE_MUTATION, {
		onError: (apolloError) => {
			fireErrorAnalytics({
				meta: {
					id: QUERY_NAME,
					packageName: 'jiraBusinessTimeline',
					teamName: 'jira-yurree',
				},
				error: apolloError,
				attributes: { message: 'Failed to remove dependency in timeline view' },
				sendToPrivacyUnsafeSplunk: true,
			});
			onFail('timeline remove dependency service', apolloError);
		},
		onCompleted: (apolloData) => {
			const relevantError = getRelevantError(apolloData);
			if (relevantError) {
				onFail('timeline remove dependency service', relevantError);
			} else {
				onSuccess();
			}
		},
	});

	const remove = async ({ linkId, inwardIssueId, outwardIssueId }: UseRemoveDependencyInput) => {
		start();
		const optimisticResponse = {
			unlinkJwmItems: createOptimisticLinkItems({
				linkId,
				issuesMap,
			}),
		};

		await removeDependency({
			variables: {
				fieldIdsToReturn,
				input: { linkId },
			},
			optimisticResponse,
			update: (proxy, result) => {
				if (!getRelevantError(result.data)) {
					// update both issues (dependency and dependee) in Apollo cache with newly created links
					updateIssueLinksInCache({
						issueId: inwardIssueId,
						issueLinks: {
							edges: (issuesMap.get(inwardIssueId)?.originalIssueLinks ?? []).filter(
								(edge) => edge?.node?.issueLinkId !== linkId,
							),
							__typename: 'JiraIssueLinkConnection',
						},
					});
					updateIssueLinksInCache({
						issueId: outwardIssueId,
						issueLinks: {
							edges: (issuesMap.get(outwardIssueId)?.originalIssueLinks ?? []).filter(
								(edge) => edge?.node?.issueLinkId !== linkId,
							),
							__typename: 'JiraIssueLinkConnection',
						},
					});
				}
			},
		});
	};

	const relevantError = useMemo(() => getRelevantError(data) || error, [data, error]);

	return {
		loading,
		error: relevantError,
		remove,
	};
};
